import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { FolderRail } from '@/components/console/workspace/FolderRail';
import { ConversationList } from '@/components/console/workspace/ConversationList';
import { ChatView } from '@/components/console/workspace/ChatView';
import { ContactPanel } from '@/components/console/workspace/ContactPanel';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { useConsole } from '@/console/ConsoleContext';
import { parseFolderParam } from '@/console/folders';
import { usePermission } from '@/hooks/usePermission';

/** Why the right-hand pane shows a sentence instead of a conversation. */
type PaneNotice = 'withdrawn' | 'failed' | null;

/**
 * Conversation workspace: folder rail · folder list · chat (ONE merged timeline: customer chat + bot consultation) ·
 * contact panel. The whole daily-driver screen.
 *
 * <b>The URL is the single source of truth for which conversation is open AND which folder is listed.</b>
 * `/conversations/:sessionId?folder=<id>` addresses one conversation inside one folder, so a refresh, a shared link and
 * the browser's back button all land where the agent was. `?folder=` is parsed by `parseFolderParam` (an unknown id, or
 * `teammates` for a viewer who may not read everyone's, opens the default folder) and pushed to the shell with
 * `selectFolder`; picking a folder in the rail changes only the query, so the open conversation stays open.
 *
 * <b>Hydrate-on-demand is what makes those links reliable.</b> The shell loads ONE page (twenty rows) of the selected
 * folder at a time, so a contact's older conversation is frequently NOT in the store. When the id is absent the page
 * asks the shell to re-read that one conversation (`refreshRow`: the single-row door that also applies the server's
 * visibility verdict) and the answer is the row, a "not available" sentence, or a failure sentence - the same sentence
 * for "unknown" and "not yours", so the endpoint is not an id oracle.
 *
 * <b>A conversation that stops being visible clears itself.</b> When the open conversation is withdrawn (someone else
 * claimed it) or its read answers 404, the row is in the store's hidden set: the page leaves its URL (replace, keeping the
 * folder) and says so, instead of leaving a thread on screen the viewer may no longer read.
 */
export const ConversationsPage: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectFolder, refreshRow } = useConsole();
  const { roles } = usePermission();

  const folder = parseFolderParam(searchParams.get('folder'), roles);
  // The folder part of the URL is kept exactly as the agent has it (an absent `?folder=` stays absent: it means the default).
  const keepSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const activeSessionId = useConversationStore((s) => s.activeSessionId);
  const conversations = useConversationStore((s) => s.conversations);
  const setActiveSession = useConversationStore((s) => s.setActiveSession);
  // OP-1005 — the conversation named by the URL has been marked as spam / unwanted (by another agent's
  // ReceiveSessionHidden, or by a stale deep link).
  const routeTombstoned = useConversationStore(
    (s) => !!routeSessionId && !!s.spamTombstones[routeSessionId],
  );
  // ADR-033 — the conversation named by the URL is in the reversible hidden set (withdrawn from this viewer, or a 404), or
  // it is the one the store just deselected because it was withdrawn.
  const routeHidden = useConversationStore(
    (s) => !!routeSessionId && (routeSessionId in s.hiddenSessions || s.lastWithdrawnId === routeSessionId),
  );

  const [notice, setNotice] = useState<PaneNotice>(null);
  // Guards against re-running the hydrate for an id we already applied — the effect re-fires when
  // `conversations` changes, and the refresh itself changes `conversations`.
  const lastAppliedRef = useRef<string | null>(null);
  // The id whose row is being read right now: the outcome of THAT read decides the sentence, so the hidden-route effect
  // below must not pre-empt it with "no longer visible".
  const hydratingRef = useRef<string | null>(null);
  const routeIdRef = useRef<string | undefined>(routeSessionId);
  routeIdRef.current = routeSessionId;
  const keepSearchRef = useRef(keepSearch);
  keepSearchRef.current = keepSearch;

  const leaveThread = useCallback(() => {
    navigate({ pathname: '/conversations', search: keepSearchRef.current }, { replace: true });
  }, [navigate]);

  // The folder in the URL is the folder the shell lists.
  useEffect(() => {
    selectFolder(folder);
  }, [folder, selectFolder]);

  // A URL that names a spam-hidden conversation is left, not hydrated: the store already refuses to hold the row, so
  // opening it would only land on the "select a conversation" prompt with a dead URL.
  useEffect(() => {
    if (routeTombstoned) leaveThread();
  }, [routeTombstoned, leaveThread]);

  // A URL that names a conversation this viewer may no longer see: say so and leave it.
  useEffect(() => {
    if (!routeSessionId || routeTombstoned || !routeHidden) return;
    if (hydratingRef.current === routeSessionId) return;
    setNotice('withdrawn');
    leaveThread();
  }, [routeSessionId, routeTombstoned, routeHidden, leaveThread]);

  // Without a route param nothing is open, so nothing is "already applied". Forgetting the last id
  // lets the SAME conversation be opened again later: after an Undo the restored row is clicked
  // with the very id this ref still held, and the guard below would otherwise skip selecting it.
  useEffect(() => {
    if (!routeSessionId) lastAppliedRef.current = null;
  }, [routeSessionId]);

  useEffect(() => {
    // No route param ⇒ do nothing. Deliberately NOT "clear the selection": handleSessionClosed
    // deselects a closed thread, and re-asserting a store value here would fight it.
    if (!routeSessionId || routeTombstoned || routeHidden) return;
    if (lastAppliedRef.current === routeSessionId) return;

    lastAppliedRef.current = routeSessionId;
    setNotice(null);

    // Select FIRST, even when the row is not loaded yet: ConsoleShell pins the active session's row
    // across a list reload (OP-372), so selecting before hydrating is what protects the row from
    // being dropped by a refetch that lands mid-flight.
    setActiveSession(routeSessionId);

    // A row the console already holds is shown as it is - unless it is only a placeholder built from a push or a draft
    // (`partial`: no owner, no folder, never read from the server): that one still needs the server's word before anyone
    // acts on it, so it is re-read like a row that is not loaded at all.
    const held = conversations.find((c) => c.sessionId === routeSessionId);
    if (held && held.partial !== true) return;

    const id = routeSessionId;
    hydratingRef.current = id;
    void refreshRow(id).then((outcome) => {
      if (hydratingRef.current === id) hydratingRef.current = null;
      // The agent already moved on to another conversation: this answer is no longer about what is on screen.
      if (routeIdRef.current !== id) return;
      if (outcome === 'hidden') {
        // The server will not show this row to this viewer (the same answer for "no such conversation" and "not yours").
        // The shell has already put it in the hidden set; say the failure sentence and leave the dead URL.
        setNotice('failed');
        leaveThread();
      } else if (outcome === 'failed') {
        // Say the link failed rather than rendering the "select a conversation" prompt, which would
        // imply the agent simply hasn't chosen one yet.
        setNotice('failed');
      }
      // 'applied' / 'superseded': the row is in the store; the active conversation renders from it.
    });
  }, [routeSessionId, routeTombstoned, routeHidden, conversations, setActiveSession, refreshRow, leaveThread]);

  const active = conversations.find((c) => c.sessionId === activeSessionId) ?? null;

  const selectRow = (id: string) => {
    navigate({ pathname: `/conversations/${encodeURIComponent(id)}`, search: keepSearch });
  };

  const selectFolderInUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('folder', id);
    setSearchParams(next);
  };

  return (
    <div className="ac-ws has-rail" data-testid="conversations-page">
      <FolderRail selected={folder} onSelect={selectFolderInUrl} />
      <ConversationList folder={folder} activeId={activeSessionId} onSelect={selectRow} />

      {active ? (
        <ChatView key={`thread-${active.sessionId}`} conv={active} />
      ) : (
        <div className="ac-thread-wrap">
          <div className="ac-empty">
            <div className="ac-empty-ico">
              <Icon name={notice ? 'flag' : 'chat'} size={28} />
            </div>
            {notice === 'failed' ? (
              <div className="ac-empty-title" data-testid="conv-open-failed">
                {t('conv_open_failed')}
              </div>
            ) : notice === 'withdrawn' ? (
              <div className="ac-empty-title" data-testid="conv-withdrawn" role="status">
                {t('conv_withdrawn')}
              </div>
            ) : (
              <>
                <div className="ac-empty-title">{t('select_convo')}</div>
                <div className="ac-empty-sub">{t('select_convo_sub')}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Keyed on the SESSION (not the customer) so switching between two conversations of the same
          person resets the subject editor and the contact section.

          The key is PREFIXED, and that prefix is load-bearing. ChatView is a sibling in this same
          parent and is also keyed on the session id; two siblings sharing one key put React into
          keyed reconciliation with a collision, and it then fails to remove the outgoing ChatView's
          DOM — every conversation switch stranded another thread in the flex row until the workspace
          was a wall of slivers. Before this panel existed the sibling was keyed on `customerId`, so
          the ids differed by accident rather than by design. Keep the two prefixes distinct. */}
      {active && <ContactPanel key={`cp-${active.sessionId}`} conv={active} />}
    </div>
  );
};
